import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  Users, 
  Clock,
  Filter,
  Search,
  MessageCircle,
  Phone,
  MapPin,
  Megaphone,
  Brain,
  BarChart3,
  Award
} from 'lucide-react';
import { Emergency, Responder, ChatMessage, VoiceCall, EmergencyBroadcast as BroadcastType } from '../types';
import { EmergencyCard } from './EmergencyCard';
import { ResponderCard } from './ResponderCard';
import { ChatInterface } from './ChatInterface';
import { VoiceCallManager } from './VoiceCallManager';
import { LocationTracker } from './LocationTracker';
import { EmergencyBroadcast } from './EmergencyBroadcast';
import { ResponderAvailabilityToggle } from './ResponderAvailabilityToggle';
import { AIInsightsDashboard } from './AIInsightsDashboard';
import { BlockchainVerification } from './BlockchainVerification';
import { AnalyticsDashboard } from './AnalyticsDashboard';

interface ResponderDashboardProps {
  emergencies: Emergency[];
  responders: Responder[];
  onUpdateEmergencyStatus: (id: string, status: Emergency['status']) => void;
  onUpdateResponderAvailability?: (id: string, availability: Responder['availability']) => void;
  className?: string;
}

export const ResponderDashboard: React.FC<ResponderDashboardProps> = ({ 
  emergencies, 
  responders,
  onUpdateEmergencyStatus,
  onUpdateResponderAvailability,
  className = '' 
}) => {
  const [activeTab, setActiveTab] = useState<'emergencies' | 'responders' | 'communications' | 'ai-insights' | 'analytics' | 'blockchain'>('emergencies');
  const [statusFilter, setStatusFilter] = useState<Emergency['status'] | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmergency, setSelectedEmergency] = useState<Emergency | null>(null);
  const [selectedResponder, setSelectedResponder] = useState<Responder | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [activeCalls, setActiveCalls] = useState<VoiceCall[]>([]);
  const [broadcasts, setBroadcasts] = useState<BroadcastType[]>([]);

  // Mock current user data (in real app, this would come from auth)
  const currentUser = {
    id: 'dispatcher-1',
    name: 'Emergency Dispatcher',
    type: 'dispatcher' as const
  };

  useEffect(() => {
    // Initialize with some mock chat messages
    const mockMessages: ChatMessage[] = [
      {
        id: '1',
        senderId: 'system',
        senderName: 'System',
        senderType: 'system',
        message: 'Emergency chat initialized',
        timestamp: new Date(Date.now() - 300000),
        emergencyId: selectedEmergency?.id
      }
    ];
    setChatMessages(mockMessages);
  }, [selectedEmergency]);

  const getEmergencyStats = () => {
    const total = emergencies.length;
    const pending = emergencies.filter(e => e.status === 'pending').length;
    const active = emergencies.filter(e => ['assigned', 'en-route', 'on-scene'].includes(e.status)).length;
    const resolved = emergencies.filter(e => e.status === 'resolved').length;

    return { total, pending, active, resolved };
  };

  const getResponderStats = () => {
    const total = responders.length;
    const available = responders.filter(r => r.availability === 'available').length;
    const busy = responders.filter(r => r.availability === 'busy').length;
    const offline = responders.filter(r => r.availability === 'offline').length;
    const verified = responders.filter(r => r.blockchainVerification?.verificationStatus === 'verified').length;

    return { total, available, busy, offline, verified };
  };

  const filteredEmergencies = emergencies
    .filter(emergency => {
      if (statusFilter !== 'all' && emergency.status !== statusFilter) return false;
      if (searchTerm && !emergency.description.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !emergency.type.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      // Sort by severity, then by time
      const severityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
      const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
      if (severityDiff !== 0) return severityDiff;
      return b.timestamp.getTime() - a.timestamp.getTime();
    });

  const filteredResponders = responders.filter(responder => {
    if (searchTerm && !responder.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !responder.category.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const handleSendChatMessage = (message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: Date.now().toString(),
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, newMessage]);
  };

  const handleCallStart = (call: VoiceCall) => {
    setActiveCalls(prev => [...prev, call]);
  };

  const handleCallEnd = (callId: string) => {
    setActiveCalls(prev => prev.filter(call => call.id !== callId));
  };

  const handleLocationUpdate = (responderId: string, location: any) => {
    console.log('Location updated for responder:', responderId, location);
  };

  const handleBroadcastCreate = (broadcast: BroadcastType) => {
    setBroadcasts(prev => [broadcast, ...prev]);
  };

  const handleResponderAvailabilityChange = (responderId: string, availability: Responder['availability']) => {
    onUpdateResponderAvailability?.(responderId, availability);
  };

  const emergencyStats = getEmergencyStats();
  const responderStats = getResponderStats();

  return (
    <div className={`bg-gray-50 min-h-screen ${className}`}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Emergency Response Dashboard</h1>
                <p className="text-gray-600">AI-powered emergency coordination with blockchain verification</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Emergencies</p>
                <p className="text-3xl font-bold text-red-600">{emergencyStats.active}</p>
              </div>
              <AlertTriangle className="w-12 h-12 text-red-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Assignment</p>
                <p className="text-3xl font-bold text-yellow-600">{emergencyStats.pending}</p>
              </div>
              <Clock className="w-12 h-12 text-yellow-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Available Responders</p>
                <p className="text-3xl font-bold text-green-600">{responderStats.available}</p>
              </div>
              <Users className="w-12 h-12 text-green-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Calls</p>
                <p className="text-3xl font-bold text-blue-600">{activeCalls.length}</p>
              </div>
              <Phone className="w-12 h-12 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Verified Responders</p>
                <p className="text-3xl font-bold text-purple-600">{responderStats.verified}</p>
              </div>
              <Award className="w-12 h-12 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Resolved Today</p>
                <p className="text-3xl font-bold text-green-600">{emergencyStats.resolved}</p>
              </div>
              <Shield className="w-12 h-12 text-green-600" />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Dashboard */}
          <div className="lg:col-span-2 space-y-6">
            {/* Enhanced Tabs */}
            <div className="bg-white rounded-lg shadow">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex overflow-x-auto">
                  {[
                    { id: 'emergencies', label: 'Emergency Queue', icon: AlertTriangle, count: emergencies.length },
                    { id: 'responders', label: 'Responders', icon: Users, count: responders.length },
                    { id: 'communications', label: 'Communications', icon: MessageCircle },
                    { id: 'ai-insights', label: 'AI Insights', icon: Brain },
                    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
                    { id: 'blockchain', label: 'Blockchain', icon: Award }
                  ].map(tab => {
                    const TabIcon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`flex items-center gap-2 py-4 px-6 border-b-2 font-medium text-sm whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'border-red-500 text-red-600 bg-red-50'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <TabIcon className="w-4 h-4" />
                        {tab.label}
                        {tab.count !== undefined && (
                          <span className="ml-1 px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded-full">
                            {tab.count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Filters */}
              {(activeTab === 'emergencies' || activeTab === 'responders') && (
                <div className="p-4 border-b bg-gray-50 flex flex-col sm:flex-row gap-4">
                  <div className="flex items-center gap-2">
                    <Search className="w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {activeTab === 'emergencies' && (
                    <div className="flex items-center gap-2">
                      <Filter className="w-5 h-5 text-gray-400" />
                      <select
                        className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value as Emergency['status'] | 'all')}
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="assigned">Assigned</option>
                        <option value="en-route">En Route</option>
                        <option value="on-scene">On Scene</option>
                        <option value="resolved">Resolved</option>
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Content */}
              <div className="p-6">
                {activeTab === 'emergencies' && (
                  <div className="space-y-4">
                    {filteredEmergencies.length === 0 ? (
                      <div className="text-center py-8">
                        <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">No emergencies found</p>
                      </div>
                    ) : (
                      filteredEmergencies.map(emergency => {
                        const assignedResponder = responders.find(r => r.id === emergency.assignedResponder);
                        return (
                          <div key={emergency.id} className="relative">
                            <EmergencyCard
                              emergency={emergency}
                              assignedResponderName={assignedResponder?.name}
                              onUpdateStatus={onUpdateEmergencyStatus}
                            />
                            <button
                              onClick={() => setSelectedEmergency(emergency)}
                              className="absolute top-4 right-4 p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
                              title="Open Communications"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {activeTab === 'responders' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredResponders.length === 0 ? (
                      <div className="col-span-full text-center py-8">
                        <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg">No responders found</p>
                      </div>
                    ) : (
                      filteredResponders.map(responder => (
                        <div key={responder.id} className="relative">
                          <ResponderCard responder={responder} />
                          <button
                            onClick={() => setSelectedResponder(responder)}
                            className="absolute top-4 right-4 p-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors"
                            title="View Blockchain Verification"
                          >
                            <Award className="w-4 h-4" />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {activeTab === 'communications' && (
                  <div className="space-y-6">
                    <EmergencyBroadcast
                      currentUserId={currentUser.id}
                      userType={currentUser.type}
                      onBroadcastCreate={handleBroadcastCreate}
                    />
                  </div>
                )}

                {activeTab === 'ai-insights' && (
                  <AIInsightsDashboard
                    emergencies={emergencies}
                    responders={responders}
                  />
                )}

                {activeTab === 'analytics' && (
                  <AnalyticsDashboard
                    emergencies={emergencies}
                    responders={responders}
                  />
                )}

                {activeTab === 'blockchain' && selectedResponder && (
                  <BlockchainVerification
                    responder={selectedResponder}
                    onVerificationComplete={(verification) => {
                      console.log('Verification completed:', verification);
                    }}
                  />
                )}

                {activeTab === 'blockchain' && !selectedResponder && (
                  <div className="text-center py-8">
                    <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">Select a responder to view blockchain verification</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Communications & Tools */}
          <div className="space-y-6">
            {/* Emergency Chat */}
            {selectedEmergency && (
              <ChatInterface
                emergencyId={selectedEmergency.id}
                currentUserId={currentUser.id}
                currentUserName={currentUser.name}
                currentUserType={currentUser.type}
                messages={chatMessages}
                onSendMessage={handleSendChatMessage}
              />
            )}

            {/* Voice Call Manager */}
            {selectedEmergency && (
              <VoiceCallManager
                emergencyId={selectedEmergency.id}
                currentUserId={currentUser.id}
                onCallStart={handleCallStart}
                onCallEnd={handleCallEnd}
              />
            )}

            {/* Location Tracker */}
            <LocationTracker
              responderId={currentUser.id}
              responderName={currentUser.name}
              onLocationUpdate={(location) => handleLocationUpdate(currentUser.id, location)}
              isTracking={true}
            />

            {/* Responder Availability Toggle */}
            <ResponderAvailabilityToggle
              responderId={currentUser.id}
              responderName={currentUser.name}
              currentAvailability="available"
              onAvailabilityChange={(availability) => handleResponderAvailabilityChange(currentUser.id, availability)}
            />
          </div>
        </div>
      </div>
    </div>
  );
};